/**
 * The row of headline numbers under a page header.
 *
 * ## Why this exists
 *
 * design/09 §7.5: *"a single KPI is a stat tile, not a chart and not a donut"*, and
 * `tokens/typography.json` carries `type.metric` — 28/32, medium, the numeric face —
 * whose `$use` is the words **"KPI tile values"**. Measured on 2026-09-24 that token had
 * exactly one consumer in the whole repository, a chart caption in `src/charts/forms.tsx`.
 * Every actual KPI in the product renders at `type.numeric`: 13px, the size of a grid cell.
 *
 * So the headline number on a screen was the same size as the least important number on
 * it. That is not a styling preference — it is the screen failing to say what it is about.
 *
 * ## It takes children rather than data
 *
 * A `KpiBand` is a layout, and the thing it lays out is `Measure`, which already knows how
 * to render a number that may be undefined and what its absence means (ADR 0098). Giving
 * this component its own `items` array would be a second measure API, and the second one
 * would be the one without the absence handling — which is exactly how each chapter's
 * `Figure` came to exist.
 *
 * Pass `size="metric"` on the measures inside it. It is not forced here, because a band
 * is also the right layout for four counts in a dense panel, and a component that silently
 * restyled its children would be impossible to opt out of.
 *
 * ## The separators are hairlines, not gutters
 *
 * Same reasoning as `CellGrid`, and it uses one: the tiles butt against each other and the
 * ground shows through a 1px gap. Four tiles in a row read as one band of four rather than
 * as four cards that happen to be adjacent.
 */

import type { ReactNode } from 'react';

import { GhostProvider } from '../primitives';
import { CellGrid } from './band';

export interface KpiBandProps {
  readonly children: ReactNode;
  /**
   * The narrowest a tile may be before the band wraps. Defaults to a width that gives four
   * across at the `lg` breakpoint and two on a phone, without either being a declared
   * column count.
   */
  readonly minTileWidth?: string;
  /**
   * The silhouette (spec v6b §2, ADR 9013): the same tiles, labels and units, with `—` where
   * each figure will be, whatever value a `Measure` was handed — ghost is not zero. Reaches
   * the measures through `GhostProvider`, so a screen writes its band once and flips this.
   */
  readonly ghost?: boolean;
}

export function KpiBand({ children, minTileWidth = 'min(12rem, 45%)', ghost = false }: KpiBandProps): React.JSX.Element {
  return (
    // `CellGrid` puts each child in its own cell; wrapping again here would nest a
    // cell inside a cell and double the tile's padding.
    <GhostProvider ghost={ghost}>
      {/* A block of the page grid (brief part 3 §B6, ADR 9038). */}
      <div data-layout-block="" className="min-w-0">
        <CellGrid minCellWidth={minTileWidth}>{children}</CellGrid>
      </div>
    </GhostProvider>
  );
}
